import { useState } from 'react';

import { Card, ClickAwayListener, Typography } from '@mui/material';
import { Stack, SxProps } from '@mui/system';
import { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

import InputField from '~components/inputFields/InputField';
import { dateFormats } from '~constants';
import { Dimens } from '~themes';

import DateRangePicker from './dateRangePicker';

interface DateRangeInputProps {
	label?: string;
	placeHolder?: string;
	initialStartDate: Dayjs;
	initialEndDate: Dayjs;
	onRangeUpdated: (startDate: Dayjs, endDate: Dayjs) => void;
	sx?: SxProps;
	resetTrigger?: number;
}

const DateRangeInput = ({
	label,
	placeHolder,
	onRangeUpdated,
	initialStartDate,
	initialEndDate,
	sx,
	resetTrigger = 0,
}: DateRangeInputProps) => {
	const { t } = useTranslation();

	const [showDateRangePicker, setShowDateRangePicker] = useState<boolean>(false);
	const [inputValue, setInputValue] = useState<string>(
		initialStartDate && initialEndDate ?
			`${initialStartDate.format(dateFormats.date)} - ${initialEndDate.format(dateFormats.date)}`
		:	''
	);

	function setFilters(startDate: Dayjs, endDate: Dayjs) {
		// Wants a time zone for some reason. It's not the default date object that Aria uses. But UTC should be fine,
		// since we're not looking at any times.
		setInputValue(`${startDate.format(dateFormats.date)} - ${endDate.format(dateFormats.date)}`);
		onRangeUpdated(startDate, endDate);
	}

	return (
		<ClickAwayListener
			onClickAway={() => {
				setShowDateRangePicker(false);
			}}
		>
			<Stack sx={{ width: 260, ...(sx ?? {}) }}>
				{label && <Typography variant='labelSm'>{label}</Typography>}
				<InputField
					readonly
					placeholder={placeHolder ?? t('selectDateRange', { ns: 'date' })}
					value={inputValue}
					onChange={() => {
						// Ignore
					}}
					onClick={() => {
						setShowDateRangePicker(true);
					}}
					StartIcon='icon/calendar'
				/>
				<div style={{ position: 'relative' }}>
					<Card
						elevation={4}
						sx={{
							position: 'absolute',
							zIndex: 9001, // Needs to be above data grid.
							p: Dimens.spacingMd,
							borderRadius: Dimens.radiusXl,
							display: showDateRangePicker ? 'block' : 'none',
						}}
					>
						<DateRangePicker
							isOpen={showDateRangePicker}
							initialStartDate={initialStartDate}
							initialEndDate={initialEndDate}
							onCancelled={() => {
								setShowDateRangePicker(false);
							}}
							onDatesChange={(start: Dayjs, end: Dayjs) => {
								setFilters(start, end);
								setShowDateRangePicker(false);
							}}
							resetTrigger={resetTrigger}
						/>
					</Card>
				</div>
			</Stack>
		</ClickAwayListener>
	);
};

export default DateRangeInput;
